import { throttle } from "lodash";
import axios from "axios";
import { getCookie } from "@/utils/post";
import { WhiteboardData } from "@/store/stateInterfaceType";

function makeHeaders() {
  const csrftoken = getCookie("csrftoken");

  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  if (csrftoken) {
    headers["X-CSRFToken"] = csrftoken;
  }

  return headers;
}

export function getJSON(url: string) {
  return axios.get(url, { headers: makeHeaders() });
}
export function postJSON(url: string, payload: Record<string, unknown>) {
  return axios.post(url, { ...payload }, { headers: makeHeaders() });
}
export function putJSON(url: string, payload: Record<string, unknown>) {
  return axios.put(url, { ...payload }, { headers: makeHeaders() });
}

export interface BoardAPIResponse {
  wbid: string;
  name: string;
  t_created: string;
}

export interface AccessKeysResponse {
  access_keys: {
    view: string;
    contribute: string;
    admin: string;
  };
}

function convertWhiteboardDataToServerData(
  d: Partial<WhiteboardData>
): Record<string, unknown> {
  const result: Record<string, unknown> = {
    name: d.name,
    is_template: d.isTemplate,
  };
  return result;
}

export const _InternalAPI = {
  whiteboard: {
    updateWBName(
      wbid: string,
      name: string,
      cb: ((rsp: BoardAPIResponse | null) => void) | null = null
    ): Promise<BoardAPIResponse | null> {
      return _InternalAPI.whiteboard.updateWBMetadata(wbid, { name }, cb);
    },

    async updateWBMetadata(
      wbid: string,
      metadata: Record<string, unknown>,
      cb: ((rsp: BoardAPIResponse | null) => void) | null = null
    ): Promise<BoardAPIResponse | null> {
      const response = await putJSON(
        `/internal/whiteboards/${wbid}/`,
        convertWhiteboardDataToServerData(metadata)
      );
      const data = response.data as BoardAPIResponse;
      if (!data.name) {
        if (cb) cb(null);
        return null;
      }
      if (cb) cb(data);
      return data;
    },
  },
};

export const InternalAPI = {
  whiteboard: {
    // Throttled
    updateWBName: throttle(_InternalAPI.whiteboard.updateWBName, 1000, {
      leading: false,
      trailing: true,
    }),

    updateWBMetadataImmediately: _InternalAPI.whiteboard.updateWBMetadata,

    async create(): Promise<{
      board: BoardAPIResponse;
      accessKeys: AccessKeysResponse;
    }> {
      const response = await postJSON("/internal/whiteboards/", {});
      const data = response.data as BoardAPIResponse;
      const wbid = data.wbid;
      const accessKeysResponse = await getJSON(
        `/api/1.0/whiteboards/${wbid}/access_keys`
      );
      const accessKeysData = accessKeysResponse.data as AccessKeysResponse;
      return { board: data, accessKeys: accessKeysData };
    },
  },
};
